import { Link } from '@reach/router';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { MAX_ALERT_COUNT } from '@xing-com/crate-jobs-constants';
import {
  useCurrentSearchAlertStatus,
  useEditSearchAlert,
  useIsAiSearch,
  useIsSearchAlertsMaxReached,
  useSearchAlertRequiredFields,
} from '@xing-com/crate-jobs-hooks';
import { ROUTES } from '@xing-com/crate-jobs-paths';
import { mediaConfined } from '@xing-com/layout-tokens';
import { spaceM, spaceS } from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import { SEARCH_ALERT_LABEL_ID } from '../constants';

import { SearchAlertBannerAction } from './search-alert-banner-action';

const LIContent: React.FC = () => {
  const { location, keywords } = useSearchAlertRequiredFields();
  const { searchAlertId } = useCurrentSearchAlertStatus();
  const { isEditMode } = useEditSearchAlert();
  const isAiSearch = useIsAiSearch();

  if (isAiSearch) {
    const copy = searchAlertId
      ? 'JOBS_SEARCH_ALERT_BANNER_CS_ACTIVE'
      : isEditMode
        ? 'JOBS_SEARCH_EDIT_ALERT_BANNER_TEXT_CS'
        : 'JOBS_SEARCH_ALERT_BANNER_CS_INACTIVE';

    return <FormattedMessage id={copy} />;
  }

  if (keywords && location) {
    const copy = searchAlertId
      ? 'JOBS_SEARCH_ALERT_BANNER_KEYWORDS_CITY_ACTIVE'
      : isEditMode
        ? 'JOBS_SEARCH_EDIT_ALERT_BANNER_TEXT_DESKTOP_KEYWORDS_CITY'
        : 'JOBS_SEARCH_ALERT_BANNER_KEYWORDS_CITY_INACTIVE';

    return (
      <FormattedMessage
        id={copy}
        values={{
          position: keywords,
          city: location,
          b: (chunks) => <b>{chunks}</b>,
        }}
      />
    );
  }

  if (location) {
    const copy = searchAlertId
      ? 'JOBS_SEARCH_ALERT_BANNER_CITY_ACTIVE'
      : isEditMode
        ? 'JOBS_SEARCH_EDIT_ALERT_BANNER_TEXT_DESKTOP_CITY'
        : 'JOBS_SEARCH_ALERT_BANNER_CITY_INACTIVE';

    return (
      <FormattedMessage
        id={copy}
        values={{ city: location, b: (chunks) => <b>{chunks}</b> }}
      />
    );
  }

  const copy = searchAlertId
    ? 'JOBS_SEARCH_ALERT_BANNER_KEYWORDS_ACTIVE'
    : isEditMode
      ? 'JOBS_SEARCH_EDIT_ALERT_BANNER_TEXT_DESKTOP_KEYWORDS'
      : 'JOBS_SEARCH_ALERT_BANNER_KEYWORDS_INACTIVE';

  return (
    <FormattedMessage
      id={copy}
      values={{ position: keywords, b: (chunks) => <b>{chunks}</b> }}
    />
  );
};

const LOContent: React.FC = () => {
  const { location, keywords } = useSearchAlertRequiredFields();

  const desktopSubtitle =
    keywords && location ? (
      <FormattedMessage
        id="JOBS_SEARCH_ALERT_LO_BANNER_KEYWORDS_LOCATION"
        values={{ location, keywords }}
      />
    ) : location ? (
      <FormattedMessage
        id="JOBS_SEARCH_ALERT_LO_BANNER_LOCATION"
        values={{ location }}
      />
    ) : (
      keywords
    );

  return (
    <LOContentWrapper>
      <b>
        <FormattedMessage id="JOBS_SEARCH_ALERT_LO_BANNER_TITLE" />
      </b>
      <span>
        &nbsp;
        <FormattedMessage id="JOBS_SEARCH_ALERT_LO_BANNER_SUBTITLE" />{' '}
        <b>{desktopSubtitle}</b>
      </span>
    </LOContentWrapper>
  );
};

export const SearchAlertBannerText: React.FC = () => {
  const isMaxReached = useIsSearchAlertsMaxReached();
  const { isLoggedIn } = useLoginState();

  if (isMaxReached) {
    return (
      <TextWrapper>
        <BodyCopy
          lineHeight="dense"
          as="label"
          // @ts-expect-error FIXME: SC6
          htmlFor={SEARCH_ALERT_LABEL_ID}
          noMargin
        >
          <FormattedMessage
            id="JOBS_SEARCH_ALERT_MAX_REACHED"
            values={{
              number: MAX_ALERT_COUNT,
              a: (chunks) => (
                <Link to={ROUTES.myJobsSearchAlerts}>{chunks}</Link>
              ),
            }}
          />
        </BodyCopy>
      </TextWrapper>
    );
  }

  return (
    <TextWrapper>
      <BodyCopy
        lineHeight="dense"
        as="label"
        // @ts-expect-error FIXME: SC6
        htmlFor={SEARCH_ALERT_LABEL_ID}
        noMargin
      >
        {isLoggedIn ? <LIContent /> : <LOContent />}
      </BodyCopy>
      <SearchAlertBannerAction />
    </TextWrapper>
  );
};

const TextWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spaceM};
  flex: 1;
  margin-left: ${spaceS};

  & > label {
    cursor: pointer;
  }

  @media ${mediaConfined} {
    justify-content: flex-start;
    flex: unset;
  }
`;

const LOContentWrapper = styled.div`
  display: flex;
`;
